import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MobileWebCamCheck from "@/components/setup/MobileWebCamCheck";
import MobileInterviewStream from "@/components/setup/MobileInterviewStream";
import MobileNetworkWarning from "@/modules/check-setup/components/MobileNetworkWarning";
import usePeerConnectionManager from "@/hooks/usePeerConnectionManager";

interface MobileWebcamSetupPageProps {
  peerConnectionId?: string;
  [key: string]: unknown;
}

/*
  ------------------------------------------------------
  Mobile setup page manages socket join, permissions, and LAN-only reconnects
  ------------------------------------------------------
*/
const MobileWebCamSetupPage: React.FC<MobileWebcamSetupPageProps> = ({ peerConnectionId: peerConnectionIdFromParams }) => {
  const peerConnectionId = useMemo(() => {
    if (peerConnectionIdFromParams) return peerConnectionIdFromParams;
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("peerConnectionId");
  }, [peerConnectionIdFromParams]);

  const {
    joinRoom,
    startPeerConnection,
    exitRoom,
    ensureSocketConnection,
    emitMobileJoined,
    triggerPeerReconnect,
    isSocketConnected,
    peerStatus,
    peerError,
    localStream,
    isPeerConnected,
    lanStatus,
    lanStatusDetail,
    lanMatchedPeerFailed,
    isMobileBypassed,
    interviewCompleteSignal,
    sendPeerMessage,
  } = usePeerConnectionManager();

  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const hasEmittedJoinedRef = useRef(false);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (isMobileBypassed) {
      setShowNetworkModal(false);
      return;
    }
    setShowNetworkModal(peerStatus === "error");
  }, [peerStatus, isMobileBypassed]);

  const isLanMismatch = lanStatus === "mismatched";
  const showPeerFallbackModal = lanMatchedPeerFailed && !isMobileBypassed;
  const shouldShowNetworkModal = !isMobileBypassed && (showNetworkModal || isLanMismatch);
  const networkWarningReason = isLanMismatch ? "mismatch" : "network-error";

  const clearCameraStream = useCallback(() => {
    setCameraStream((prev) => {
      if (prev) prev.getTracks().forEach((track) => track.stop());
      return null;
    });
    setSelectedCamera("");
  }, []);

  useEffect(() => {
    if (!peerConnectionId) return;

    joinRoom(peerConnectionId, "mobile");
    hasEmittedJoinedRef.current = false;

    return () => {
      exitRoom(true);
    };
  }, [joinRoom, exitRoom, peerConnectionId]);

  useEffect(() => {
    if (isSocketConnected && peerConnectionId && emitMobileJoined && !hasEmittedJoinedRef.current) {
      emitMobileJoined(peerConnectionId);
      hasEmittedJoinedRef.current = true;
    } else if (!isSocketConnected) {
      hasEmittedJoinedRef.current = false;
    }
  }, [isSocketConnected, peerConnectionId, emitMobileJoined]);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  useEffect(() => {
    cameraStreamRef.current = cameraStream;
  }, [cameraStream]);

  useEffect(() => {
    if (!interviewCompleteSignal) return;

    exitRoom(true);
    clearCameraStream();
    setIsConnecting(false);
    setShowNetworkModal(false);
    if (typeof window !== "undefined") {
      window.location.replace("/mobile-webcam/interview-ended");
    }
  }, [interviewCompleteSignal, exitRoom, clearCameraStream]);

  const handleConnectWithInterview = async (camera: string) => {
    if (!camera || !peerConnectionId) return;

    try {
      setIsConnecting(true);
      setShowNetworkModal(false);

      if (!isSocketConnected) {
        await ensureSocketConnection();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: camera ? { exact: camera } : undefined,
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      setSelectedCamera(camera);
      setCameraStream(stream);
      await startPeerConnection({ initiator: true, stream });
    } catch (error) {
      console.error("[MobileWebCamSetupPage] Failed to start peer connection", error);
      setShowNetworkModal(true);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleTestDisconnect = useCallback(() => {
    triggerPeerReconnect();
  }, [triggerPeerReconnect]);

  const handleReconnect = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    window.location.reload();
  }, []);

  const handleNetworkWarningClose = useCallback(() => {
    handleReconnect();
  }, [handleReconnect]);

  const canShowInterviewStream = isPeerConnected && !shouldShowNetworkModal && !isMobileBypassed;
  const shouldShowFallbackCard = !shouldShowNetworkModal && (showPeerFallbackModal || isMobileBypassed);
  const shouldShowSetupChecklist = !shouldShowFallbackCard && !canShowInterviewStream;

  if (!peerConnectionId) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-20 text-center">
          <p className="text-2xl font-semibold">Mobile setup link is invalid</p>
          <p className="text-sm text-slate-300">
            Reopen this page from the QR code shown on your laptop. Each session link is unique.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex justify-center p-0">
      <div className="w-full max-w-4xl bg-white p-8 sm:p-2 overflow-hidden">
        <MobileNetworkWarning
          open={shouldShowNetworkModal}
          onClose={handleNetworkWarningClose}
          reason={networkWarningReason}
          networkDetails={isLanMismatch ? lanStatusDetail ?? undefined : undefined}
        />
        {shouldShowFallbackCard ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
            <h2 className="text-2xl font-semibold text-slate-900">Continue on your laptop</h2>
            <p className="max-w-md text-sm text-slate-600">
              We couldn't connect your phone camera even though both devices were on the same
              network. Your interviewer can continue with the laptop camera. You can close this tab
              or try reconnecting if you're asked to help again.
            </p>
          </div>
        ) : shouldShowSetupChecklist ? (
          <MobileWebCamCheck
            onConnectWithInterview={handleConnectWithInterview}
            isSocketReady={isSocketConnected}
            status={peerStatus}
            statusError={peerError}
            isConnecting={isConnecting}
            onReconnect={handleReconnect}
          />
        ) : (
          <MobileInterviewStream
            localStream={cameraStream ?? localStream}
            selectedCamera={selectedCamera}
            handDetector={null}
            objectDetector={null}
            detectionStatusMessage={""}
            isDetectionPreparing={false}
            detectionError={null}
            onDisconnect={handleTestDisconnect}
            sendPeerMessage={sendPeerMessage}
            isPeerConnected={isPeerConnected}
          />
        )}
      </div>
    </div>
  );
};

export default MobileWebCamSetupPage;
